import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { User } from 'lucide-react';
import type { MyMatchesResponse, MyMatch } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AppHeader } from '@/components/AppHeader';
import { PlayerProfileViewerModal } from '@/components/players/PlayerProfileViewerModal';
import { formatStartedAgo } from '@/lib/relativeTime';
import { getMyMatches, getOpponentProfile } from '@/lib/api/player';
import { ApiError } from '@/lib/api/core';
import { usePolledData } from '@/lib/usePolledData';
import { useViewProfileDialog } from '@/pages/players/useViewProfileDialog';
import { showErrorToast } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { viewerResult } from './my-matches/viewerResult';
import { RespondRow } from './my-matches/RespondRow';
import { SelfReportRow } from './my-matches/SelfReportRow';

type OpponentTarget = { playerId: string };

function formatColor(
  color: 'w' | 'b' | null,
  hasColors: boolean,
  t: TFunction,
): string | null {
  if (!hasColors || color === null) return null;
  return color === 'w' ? t('labels.white') : t('labels.black');
}

function formatOutcome(match: MyMatch, t: TFunction): string {
  if (match.isBye) return t('labels.bye_short');
  if (match.resultStatus === 'none' || match.result === null) return '—';
  return viewerResult(match);
}

type BadgeProps = {
  label: string;
  variant: 'default' | 'secondary' | 'outline';
  className?: string;
  title?: string;
};

function matchStatusBadge(match: MyMatch, t: TFunction): BadgeProps {
  if (match.roundStatus === 'completed') {
    return { label: t('match_status.final'), variant: 'secondary' };
  }
  // Round is published. The pairing's own result status takes precedence
  // when there's a real result attempt; matches the badge logic in
  // ResultStatusBadge / TvPairingsPage / RoundsPage.
  if (!match.isBye) {
    if (match.resultStatus === 'confirmed') {
      return { label: t('match_status.confirmed'), variant: 'default', className: 'bg-green-600' };
    }
    if (match.resultStatus === 'pending') {
      return { label: t('match_status.reported'), variant: 'secondary', className: 'text-amber-600' };
    }
    // Story 081 — director marked this match as In Progress.
    if (match.startedAt) {
      return {
        label: t('match_status.in_progress'),
        variant: 'default',
        className: 'bg-blue-600',
        title: formatStartedAgo(match.startedAt),
      };
    }
  }
  // No per-match result yet (or this is a bye row). The round being
  // "in progress" is a director-side notion; from the player's
  // perspective the round is simply Published.
  return {
    label: t('match_status.published'),
    variant: 'outline',
    className: 'text-muted-foreground',
  };
}

export function MyMatchesPage() {
  const { t } = useTranslation('player');
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, message, refetch } = usePolledData<MyMatchesResponse>(
    () =>
      getMyMatches(slug!).catch((err: unknown) => {
        throw err instanceof ApiError ? new Error(err.userMessage) : err;
      }),
    [slug],
  );
  const refresh = refetch;
  useEffect(() => {
    if (message) showErrorToast(message);
  }, [message]);

  const profileDialog = useViewProfileDialog<OpponentTarget>({
    fetchProfile: (target) => getOpponentProfile(slug!, target.playerId),
  });

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>{t('my_matches.loading')}</p>
      </div>
    );
  }

  const game = data?.game ?? 'chess';
  const config = getGameConfig(game);
  const matches = data?.matches ?? [];

  return (
    <div data-testid="my-matches-page" className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-8">
        <div className="mb-6">
          <h2 className="text-xl font-semibold md:text-2xl">{t('my_matches.title')}</h2>
          {data?.tournamentName && (
            <p className="text-sm text-muted-foreground">{data.tournamentName}</p>
          )}
        </div>

        {matches.length === 0 ? (
          <p
            data-testid="my-matches-empty-state"
            className="text-muted-foreground"
          >
            {t('my_matches.empty')}
          </p>
        ) : (
          <div className="space-y-3">
            {matches.map((m) => {
              const colorLabel = formatColor(m.color, config.hasColors, t);
              const badge = matchStatusBadge(m, t);
              return (
                <Card
                  key={m.roundNumber}
                  data-testid={`my-match-card-${m.roundNumber}`}
                >
                  <CardContent className="flex flex-col gap-2 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <p className="font-medium">{t('labels.round_n', { roundNumber: m.roundNumber })}</p>
                      <Badge
                        variant={badge.variant}
                        className={badge.className}
                        title={badge.title}
                        data-testid="my-match-status-badge"
                      >
                        {badge.label}
                      </Badge>
                    </div>

                    {m.isBye ? (
                      <p data-testid="my-match-bye" className="text-sm">
                        {t('labels.bye_long')}
                      </p>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          data-testid="my-match-opponent"
                          className="font-medium"
                        >
                          {m.opponent
                            ? `${m.opponent.firstName} ${m.opponent.lastName}`
                            : t('labels.unknown_opponent')}
                        </span>
                        {m.opponent?.hasProfile && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            data-testid="my-match-opponent-profile-button"
                            aria-label={t('labels.view_profile_of', {
                              firstName: m.opponent.firstName,
                              lastName: m.opponent.lastName,
                            })}
                            onClick={() =>
                              profileDialog.openProfileViewer({
                                playerId: m.opponent!.playerId,
                              })
                            }
                          >
                            <User className="h-4 w-4" />
                          </Button>
                        )}
                        {colorLabel && (
                          <Badge data-testid="my-match-color" variant="outline">
                            {colorLabel}
                          </Badge>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-4 text-sm text-muted-foreground">
                      <span>{t('labels.table_n', { tableNumber: m.tableNumber })}</span>
                      <span data-testid="my-match-result" className="font-mono">
                        {formatOutcome(m, t)}
                      </span>
                    </div>

                    {m.canSelfReport && data?.tournamentId && (
                      <SelfReportRow
                        tournamentId={data.tournamentId}
                        match={m}
                        hasDraws={config.hasDraws}
                        onReported={refresh}
                      />
                    )}

                    {m.canRespond && data?.tournamentId && (
                      <RespondRow
                        tournamentId={data.tournamentId}
                        match={m}
                        onResponded={refresh}
                      />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <PlayerProfileViewerModal
        open={profileDialog.viewProfileTarget !== null}
        loading={profileDialog.viewProfileLoading}
        profile={profileDialog.viewProfileData}
        onClose={profileDialog.closeProfileViewer}
      />
    </div>
  );
}

